import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Message,
} from 'semantic-ui-react';
import { API } from '../helpers';
import './memberErrorReporterV111.css';

const endpoint = '/api/member-error-reporter/v111';
const headers = { 'X-Member-Reporter': 'v111' };
// Keep credential-bearing errors out of the shared console/toast interceptor.
const reporterAPI = axios.create({
  ...API?.defaults,
  validateStatus: () => true,
  timeout: 20000,
});
export const memberReporterWordsV111 = (language) =>
  language?.startsWith('zh')
    ? {
        title: '专属会员飞书报错通报',
        enabled: '启用专属通报',
        webhook: '专属飞书 Webhook URL / Token',
        secret: '飞书签名密钥（可选）',
        configured: '已配置',
        missing: '未配置',
        cooldown: '同类错误间隔（秒）',
        clearWebhook: '清除已保存的 Webhook',
        clearSecret: '清除已保存的签名密钥',
        save: '保存专属通报',
        test: '发送专属测试消息',
        reload: '重新加载',
        saved: '专属通报设置已保存',
        sent: '专属测试消息已发送',
        failed: '操作失败，请重试',
        loadFailed: '无法加载专属通报设置',
        invalid: '请检查飞书 Webhook、签名密钥和错误间隔',
        changed: '设置已更改，请重新加载',
        required: '请配置专属飞书 Webhook',
        delivery: '测试发送失败，请检查飞书机器人地址、安全设置和签名密钥',
        keep: '留空保留已保存的值',
      }
    : {
        title: 'Dedicated member Feishu error alerts',
        enabled: 'Enable dedicated alerts',
        webhook: 'Dedicated Feishu Webhook URL / Token',
        secret: 'Feishu signing secret (optional)',
        configured: 'Configured',
        missing: 'Not configured',
        cooldown: 'Similar-error interval (seconds)',
        clearWebhook: 'Clear saved webhook',
        clearSecret: 'Clear saved signing secret',
        save: 'Save dedicated alerts',
        test: 'Send dedicated test',
        reload: 'Reload',
        saved: 'Dedicated alert settings saved',
        sent: 'Dedicated test message sent',
        failed: 'Request failed. Try again.',
        loadFailed: 'Unable to load dedicated alert settings',
        invalid: 'Check the Feishu webhook, signing secret and error interval',
        changed: 'Settings changed. Reload before saving.',
        required: 'Configure a dedicated Feishu webhook',
        delivery:
          'Test failed. Check the Feishu webhook, security settings and signing secret.',
        keep: 'Leave blank to keep the saved value',
      };

export function validMemberReporterV111(form) {
  const cooldown = String(form.cooldown_seconds);
  const inputWebhook = (form.webhook || '').trim();
  const webhook = inputWebhook.replace(
    /^https:\/\/open\.feishu\.cn\/open-apis\/bot\/v2\/hook\//,
    ''
  );
  const secret = (form.signing_secret || '').trim();
  return (
    /^\d+$/.test(cooldown) &&
    Number(cooldown) >= 1 &&
    Number(cooldown) <= 3600 &&
    (!inputWebhook || /^[A-Za-z0-9_-]{16,160}$/.test(webhook)) &&
    (!secret || /^[A-Za-z0-9_+/=-]{1,256}$/.test(secret)) &&
    !(form.clear_webhook && webhook) &&
    !(form.clear_secret && secret) &&
    (!form.enabled ||
      webhook ||
      (form.webhook_configured && !form.clear_webhook))
  );
}

function readView(data) {
  if (
    !data ||
    typeof data.enabled !== 'boolean' ||
    typeof data.webhook_configured !== 'boolean' ||
    typeof data.signing_secret_configured !== 'boolean' ||
    typeof data.revision !== 'string' ||
    !data.revision ||
    !Number.isSafeInteger(data.cooldown_seconds)
  ) {
    throw Error('member_reporter_invalid_response');
  }
  const view = {
    enabled: data.enabled,
    cooldown_seconds: data.cooldown_seconds,
    webhook_configured: data.webhook_configured,
    signing_secret_configured: data.signing_secret_configured,
    revision: data.revision,
    webhook: '',
    signing_secret: '',
    clear_webhook: false,
    clear_secret: false,
  };
  if (!validMemberReporterV111(view))
    throw Error('member_reporter_invalid_response');
  return view;
}

export default function MemberErrorReporterV111() {
  const { i18n } = useTranslation();
  const words = memberReporterWordsV111(i18n?.language);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const generation = useRef(0);
  const adopt = (data) => {
    const next = readView(data);
    setForm(next);
    setSaved(next);
  };
  const errorText = (failure) => {
    const code = failure?.response?.data?.message || failure?.message;
    if (code === 'member_reporter_settings_changed') return words.changed;
    if (code === 'member_reporter_configuration_required')
      return words.required;
    if (code === 'member_reporter_delivery_failed') return words.delivery;
    if (code?.startsWith('member_reporter_invalid')) return words.invalid;
    return words.failed;
  };
  const load = async () => {
    const current = ++generation.current;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await reporterAPI.get(`${endpoint}/settings`);
      if (!response.data.success) throw Error(response.data.message);
      if (current === generation.current) adopt(response.data.data);
    } catch {
      if (current === generation.current) {
        setError(words.loadFailed);
        setForm(null);
        setSaved(null);
      }
    } finally {
      if (current === generation.current) setBusy(false);
    }
  };
  useEffect(() => {
    const currentGeneration = generation;
    load();
    return () => {
      currentGeneration.current++;
    };
    // The stored settings load once per mount, independently of language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const edit = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setNotice('');
    setError('');
  };
  const dirty = form && saved && JSON.stringify(form) !== JSON.stringify(saved);
  const action = async (test) => {
    if (!form || busy) return;
    if (!test && !validMemberReporterV111(form)) {
      setError(words.invalid);
      return;
    }
    const current = generation.current;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = test
        ? await reporterAPI.post(
            `${endpoint}/test`,
            { revision: saved.revision },
            { headers }
          )
        : await reporterAPI.put(
            `${endpoint}/settings`,
            {
              enabled: form.enabled,
              cooldown_seconds: Number(form.cooldown_seconds),
              webhook: form.webhook.trim(),
              signing_secret: form.signing_secret.trim(),
              clear_webhook: form.clear_webhook,
              clear_secret: form.clear_secret,
              revision: form.revision,
            },
            { headers }
          );
      if (!response.data.success) throw Error(response.data.message);
      if (current === generation.current) {
        if (!test) adopt(response.data.data);
        setNotice(test ? words.sent : words.saved);
      }
    } catch (failure) {
      if (current === generation.current) setError(errorText(failure));
    } finally {
      if (current === generation.current) setBusy(false);
    }
  };
  return (
    <section
      className='member-error-reporter-v111'
      aria-label={words.title}
      aria-busy={busy}
    >
      <Header as='h3'>{words.title}</Header>
      {error && <Message negative role='alert' content={error} />}
      {notice && <Message positive role='status' content={notice} />}
      {form && (
        <>
          <Form.Field>
            <Checkbox
              name='member_enabled'
              toggle
              label={words.enabled}
              checked={form.enabled}
              disabled={busy}
              onChange={(_, data) => edit('enabled', data.checked)}
            />
          </Form.Field>
          <Form.Group widths='equal'>
            <Form.Input
              label={`${words.webhook} (${
                form.webhook_configured ? words.configured : words.missing
              })`}
              name='member_webhook'
              type='password'
              autoComplete='new-password'
              placeholder={words.keep}
              value={form.webhook}
              disabled={busy || form.clear_webhook}
              onChange={(_, data) => edit('webhook', data.value)}
            />
            <Form.Input
              label={`${words.secret} (${
                form.signing_secret_configured
                  ? words.configured
                  : words.missing
              })`}
              name='member_signing_secret'
              type='password'
              autoComplete='new-password'
              placeholder={words.keep}
              value={form.signing_secret}
              disabled={busy || form.clear_secret}
              onChange={(_, data) => edit('signing_secret', data.value)}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label={words.cooldown}
              name='member_cooldown'
              type='number'
              min='1'
              max='3600'
              step='1'
              value={form.cooldown_seconds}
              disabled={busy}
              onChange={(_, data) => edit('cooldown_seconds', data.value)}
            />
          </Form.Group>
          <Form.Field>
            <Checkbox
              name='member_clear_webhook'
              label={words.clearWebhook}
              checked={form.clear_webhook}
              disabled={busy || !form.webhook_configured}
              onChange={(_, data) => {
                setForm((current) => ({
                  ...current,
                  clear_webhook: data.checked,
                  webhook: '',
                }));
                setNotice('');
                setError('');
              }}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              name='member_clear_secret'
              label={words.clearSecret}
              checked={form.clear_secret}
              disabled={busy || !form.signing_secret_configured}
              onChange={(_, data) => {
                setForm((current) => ({
                  ...current,
                  clear_secret: data.checked,
                  signing_secret: '',
                }));
                setNotice('');
                setError('');
              }}
            />
          </Form.Field>
        </>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button
          type='button'
          icon
          title={words.reload}
          aria-label={words.reload}
          disabled={busy}
          onClick={load}
        >
          <Icon name='refresh' />
        </Button>
        <Button
          type='button'
          icon='save'
          content={words.save}
          disabled={busy || !form || !dirty}
          loading={busy && !!form}
          onClick={() => action(false)}
        />
        <Button
          type='button'
          icon='send'
          content={words.test}
          disabled={busy || !saved?.webhook_configured || dirty}
          onClick={() => action(true)}
        />
      </div>
    </section>
  );
}
